import {
  COSTS_GJ,
  COSTS_RETENTION,
  COSTS_VERDICT,
  COSTS_VERDICT_AMOUNT,
  COSTS_VERDICT_CHEAP,
  COSTS_VERDICT_EXPENSIVE,
  COSTS_VERDICT_NEUTRAL,
} from '../config/constants';
import { Columns, Rows } from '../types';

import {
  calculateCvMaintenanceCost,
  calculateTotalAnnualGasPrice,
  calculateTotalAnnualHeatPrice,
  calculateTotalMonthlyPrice,
  m3toGJ,
} from './calculations';

export interface Column {
  id: Columns;
  values?: object;
  [key: string]: any;
}

export interface Row {
  id: Rows;
  columns: Column[];
}

/**
 * Calculate the difference between gas price and heat price and return a verdict.
 */
export const getCostsVerdict = (
  gasConsumption: number,
  gasPrice: number,
  gasGridManagementCost: number,
  gasFixedCost: number,
  cvInstallationCost: number,
  cvMaintenanceCost: number,
): COSTS_VERDICT => {
  const totalAnnualGasPrice = calculateTotalAnnualGasPrice(
    gasConsumption,
    gasPrice,
    gasGridManagementCost,
    gasFixedCost,
    cvInstallationCost,
    cvMaintenanceCost,
  );

  const totalAnnualHeatPrice = calculateTotalAnnualHeatPrice(gasConsumption);
  const difference = totalAnnualGasPrice - totalAnnualHeatPrice;

  if (difference >= COSTS_VERDICT_AMOUNT) {
    return COSTS_VERDICT_CHEAP;
  }

  if (difference < COSTS_VERDICT_AMOUNT && difference > COSTS_VERDICT_AMOUNT * -2) {
    return COSTS_VERDICT_NEUTRAL;
  }

  return COSTS_VERDICT_EXPENSIVE;
};

export default function getData(
  gasConsumption: number,
  gasPrice: number,
  gasGridManagementCost: number,
  gasFixedCost: number,
  cvMaintenanceCost: number,
  cvInstallationCost: number,
): Row[] {
  const data: Row[] = [];
  const heatConsumption = m3toGJ(gasConsumption);


  data.push({
    id: 'annual_consumption',
    columns: [
      {
        id: 'title',
      },
      {
        id: 'gas',
        values: {
          price: gasConsumption * gasPrice,
          value: gasConsumption,
          unitPrice: gasPrice,
        },
      },
      {
        id: 'heat',
        values: {
          price: heatConsumption * COSTS_GJ,
          value: heatConsumption,
          unitPrice: COSTS_GJ,
        },
      },
    ],
  });


  data.push({
    id: 'annual_fixed_cost',
    columns: [
      {
        id: 'title',
      },
      {
        id: 'gas',
        values: {
          price: gasGridManagementCost + gasFixedCost,
        },
      },
      {
        id: 'heat',
        values: {
          price: COSTS_RETENTION,
        },
      },
    ],
  });

  data.push({
    id: 'cv_maintenance_cost',
    columns: [
      {
        id: 'title',
      },
      {
        id: 'gas',
        values: {
          price: cvMaintenanceCost,
        },
      },
      {
        id: 'heat',
        values: {
          price: calculateCvMaintenanceCost(),
        },
      },
    ],
  });
  data.push({
    id: 'annual_cv_cost',
    columns: [
      {
        id: 'title',
      },
      {
        id: 'gas',
        values: {
          price: cvInstallationCost,
        },
      },
      {
        id: 'heat',
        values: {
          price: 0,
        },
      },
    ],
  });
  data.push({
    id: 'total_heading',
    columns: [
      {
        id: 'title',
        head: true,
      },
    ],
  });

  data.push({
    id: 'total',
    columns: [
      {
        id: 'title',
      },
      {
        id: 'gas',
        values: {
          price: calculateTotalAnnualGasPrice(
            gasConsumption,
            gasPrice,
            gasGridManagementCost,
            gasFixedCost,
            cvMaintenanceCost,
            cvInstallationCost,
          ),
          monthlyPrice: calculateTotalMonthlyPrice(
            calculateTotalAnnualGasPrice(
              gasConsumption,
              gasPrice,
              gasGridManagementCost,
              gasFixedCost,
              cvMaintenanceCost,
              cvInstallationCost,
            ),
          ),
        },
      },
      {
        id: 'heat',
        values: {
          price: calculateTotalAnnualHeatPrice(gasConsumption),
          monthlyPrice: calculateTotalMonthlyPrice(
            calculateTotalAnnualHeatPrice(gasConsumption),
          ),
        },
      },
    ],
  });

  return data;
}
